import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';

const TooltipContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const TooltipContent = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  background-color: var(--text-primary);
  color: var(--text-inverse);
  padding: 0.5rem 0.75rem;
  border-radius: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  font-weight: 500;
  white-space: nowrap;
  z-index: 1000;
  opacity: ${props => props.$visible ? 1 : 0};
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
  transition: opacity 0.2s ease, visibility 0.2s ease;
  ${props => props.$position === 'top' ? `
    bottom: 100%;
    margin-bottom: 0.5rem;
    margin-top: 0;
  ` : `
    top: 100%;
    margin-top: 0.5rem;
    margin-bottom: 0;
  `}
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.13);
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    ${props => props.$position === 'top' ? `
      top: 100%;
      border-top-color: var(--text-primary);
      border-bottom-color: transparent;
    ` : `
      bottom: 100%;
      border-bottom-color: var(--text-primary);
      border-top-color: transparent;
    `}
  }

  /* Hide tooltips on touch devices - hover doesn't exist */
  @media (max-width: 768px), (hover: none) {
    display: none !important;
  }
`;

function Tooltip({ children, content, text, position = 'bottom', className }) {
  const [visible, setVisible] = useState(false);
  const [positionStyle, setPositionStyle] = useState({});
  const tooltipRef = useRef(null);
  const containerRef = useRef(null);

  const showTooltip = () => {
    setVisible(true);
  };

  const hideTooltip = () => {
    setVisible(false);
  };

  const updatePosition = useCallback(() => {
    if (containerRef.current && tooltipRef.current) {
      let newPosition = {};
      switch (position) {
        case 'top':
          newPosition = {
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: '0.5rem'
          };
          break;
        case 'bottom':
          newPosition = {
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginTop: '0.5rem'
          };
          break;
        default:
          newPosition = {
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: '0.5rem'
          };
      }
      setPositionStyle(newPosition);
    }
  }, [position]);

  useEffect(() => {
    if (visible) {
      updatePosition();
    }
  }, [visible, position, updatePosition]);

  return (
    <TooltipContainer
      ref={containerRef}
      className={className}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
    >
      {children}
      <TooltipContent
        ref={tooltipRef}
        $visible={visible}
        $position={position}
        style={positionStyle}
      >
        {content || text}
      </TooltipContent>
    </TooltipContainer>
  );
}

export default Tooltip;
